import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, Mic, MicOff, Loader2, Check, Inbox, X, Trash2, ChevronDown } from 'lucide-react'
import Modal from '../../../components/ui/Modal'
import { Button } from '../../../components/ui/button'
import { supabase } from '../../../lib/supabaseClient'
import { useAuth } from '../../../contexts/AuthContext'
import { useSpeechRecognition } from '../../../hooks/useSpeechRecognition'
import { cn } from '../../../lib/utils'

// Quick Capture — the universal front door. One button on the
// dashboard opens a voice + text pane; whatever the user says gets
// classified server-side (quick-capture edge function → llm-router)
// into weight / water / meal / mood / vitals and applied directly, or
// parked in the captures inbox (expenses, notes) so nothing is lost.

const MODULE_LABEL_KEYS = {
  weight: 'quickCapture.modules.weight',
  water: 'quickCapture.modules.water',
  meal: 'quickCapture.modules.meal',
  mood: 'quickCapture.modules.mood',
  vital: 'quickCapture.modules.vital',
  expense: 'quickCapture.modules.expense',
  note: 'quickCapture.modules.note',
}

export default function QuickCapture() {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full px-3 py-2.5 rounded-lg border-2 border-dashed border-app-primary-500/40 text-app-primary-500 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-app-primary-500/5 transition-colors"
        dir="auto"
      >
        <Sparkles className="w-4 h-4" />
        {t('quickCapture.button')}
      </button>
      {open && <CapturePane t={t} locale={i18n.language === 'ar' ? 'ar' : 'en'} onClose={() => setOpen(false)} />}
    </>
  )
}

function CapturePane({ t, locale, onClose }) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const usedVoiceRef = useRef(false)
  const inputRef = useRef(null)

  const speech = useSpeechRecognition({ lang: locale === 'ar' ? 'ar-EG' : 'en-US' })

  // Single-shot recognizer: each final transcript appends to the text.
  useEffect(() => {
    if (!speech.transcript) return
    usedVoiceRef.current = true
    setText((prev) => (prev ? `${prev} ` : '') + speech.transcript)
    speech.reset()
  }, [speech.transcript])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const submit = async () => {
    const raw = text.trim()
    if (!raw || busy) return
    setBusy(true)
    setResult(null)
    const { data, error } = await supabase.functions.invoke('quick-capture', {
      body: { text: raw, source: usedVoiceRef.current ? 'voice' : 'text' },
    })
    setBusy(false)
    if (error || !data?.items?.length) {
      setResult({ ok: false })
      return
    }
    setResult({ ok: true, items: data.items })
    setText('')
    usedVoiceRef.current = false
  }

  const todayIso = new Date().toLocaleDateString('en-CA')

  return (
    <Modal open onClose={onClose} ariaLabel={t('quickCapture.title')}>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="flex-1 min-w-0 text-base font-bold text-foreground flex items-center gap-1.5 truncate" dir="auto">
          <Sparkles className="w-4 h-4 text-app-primary-500" />
          {t('quickCapture.title')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('settings.close')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-3">
        <textarea
          ref={inputRef}
          value={speech.listening && speech.interim ? `${text} ${speech.interim}`.trim() : text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('quickCapture.placeholder')}
          rows={3}
          maxLength={1000}
          className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none"
        />

        <div className="flex gap-2">
          {speech.supported && (
            <button
              type="button"
              onClick={() => (speech.listening ? speech.stop() : speech.start())}
              aria-pressed={speech.listening}
              className={cn(
                'h-10 w-12 rounded-md border inline-flex items-center justify-center flex-shrink-0 transition-colors',
                speech.listening
                  ? 'border-red-500/50 bg-red-500/10 text-red-500 animate-pulse'
                  : 'border-input text-app-primary-500 hover:bg-accent',
              )}
              aria-label={t('quickCapture.mic')}
            >
              {speech.listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
          )}
          <Button onClick={submit} disabled={busy || !text.trim()} className="flex-1">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('quickCapture.submit')}
          </Button>
        </div>

        {speech.listening && (
          <p className="text-xs text-muted-foreground" dir="auto">{t('quickCapture.listening')}</p>
        )}

        {result && !result.ok && (
          <p className="text-xs text-red-500" dir="auto">{t('quickCapture.failed')}</p>
        )}
        {result?.ok && (
          <div className="space-y-1.5">
            {result.items.map((item, i) => {
              const moduleLabel = t(MODULE_LABEL_KEYS[item.module] || MODULE_LABEL_KEYS.note)
              return (
                <div
                  key={i}
                  className={cn(
                    'rounded-md border px-3 py-2 flex items-start gap-2 text-sm',
                    item.applied
                      ? 'border-emerald-500/40 bg-emerald-500/10'
                      : 'border-app-primary-500/30 bg-app-primary-500/5',
                  )}
                  dir="auto"
                >
                  {item.applied
                    ? <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    : <Inbox className="w-4 h-4 text-app-primary-500 flex-shrink-0 mt-0.5" />}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-foreground text-xs">
                      {item.applied
                        ? t('quickCapture.savedTo', { module: moduleLabel })
                        : t('quickCapture.inboxed', { module: moduleLabel })}
                      {item.date && item.date !== todayIso && (
                        <span className="ms-1.5 font-normal text-amber-600 dark:text-amber-400" dir="ltr">{item.date}</span>
                      )}
                    </p>
                    {item.summary && <p className="text-xs text-muted-foreground mt-0.5">{item.summary}</p>}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* "الصندوق" — where unapplied captures (expenses, notes) live.
            Collapsible history with delete, so the inbox is a real
            place instead of a mystery label. */}
        <InboxList t={t} refreshKey={result} />
      </div>
    </Modal>
  )
}

const INBOX_MODULE_KEYS = MODULE_LABEL_KEYS

function InboxList({ t, refreshKey }) {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState([])

  const refetch = async () => {
    if (!user) return
    const { data } = await supabase
      .from('quick_captures')
      .select('id, created_at, raw_text, classified_module, applied, payload')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(15)
    setRows(data || [])
  }

  useEffect(() => { if (open) refetch() }, [open, user, refreshKey]) // eslint-disable-line

  const remove = async (id) => {
    await supabase.from('quick_captures').delete().eq('id', id)
    refetch()
  }

  return (
    <div className="border-t border-border/60 pt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        dir="auto"
      >
        <Inbox className="w-3.5 h-3.5" />
        {t('quickCapture.inboxTitle')}
        <ChevronDown className={cn('w-3.5 h-3.5 ms-auto transition-transform', open && 'rotate-180')} />
      </button>

      {open && rows.length === 0 && (
        <p className="text-xs text-muted-foreground py-2" dir="auto">{t('quickCapture.inboxEmpty')}</p>
      )}

      {open && rows.length > 0 && (
        <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto">
          {rows.map((row) => {
            const isMulti = row.classified_module === 'multi'
            const moduleLabel = isMulti
              ? t('quickCapture.multi')
              : t(INBOX_MODULE_KEYS[row.classified_module] || INBOX_MODULE_KEYS.note)
            return (
              <div key={row.id} className="flex items-start gap-2 text-xs" dir="auto">
                {row.applied
                  ? <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  : <Inbox className="w-3.5 h-3.5 text-app-primary-500 flex-shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <p className="text-foreground truncate">{row.raw_text}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {moduleLabel}
                    {' · '}
                    <span dir="ltr">{(row.created_at || '').slice(0, 10)}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => remove(row.id)}
                  aria-label={t('common.delete')}
                  className="p-1 rounded-md hover:bg-accent text-muted-foreground flex-shrink-0"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
